import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@qodo/design-system';
import { X } from 'lucide-react';
import { cn } from 'utils/className';
import { spawn } from 'utils/translations';
import { openUrl } from 'utils/url';
import {
  CONSENT_VALUE,
  COOKIESCRIPT_SUPPRESSED_CLASS,
  PRIVACY_POLICY_URL,
} from './consts';
import {
  hasStoredConsent,
  isFirstPartyConsentHost,
  storeConsent,
  suppressCookieScriptBanner,
} from './utils';

/**
 * First-party cookie-consent banner. Renders only on hosts where the
 * third-party Cookie-Script banner cannot persist a dismissal (see
 * {@link isFirstPartyConsentHost}); production keeps Cookie-Script untouched.
 * The user's choice is stored in localStorage and read on mount, so the banner
 * does not reappear on navigation or reload (PER-51).
 */
export const ConsentBanner = () => {
  const isManagedHost = isFirstPartyConsentHost(window.location.hostname);
  const [isConsentGiven, setIsConsentGiven] = useState(hasStoredConsent);

  // On managed hosts, suppress the broken Cookie-Script banner so it never
  // double-renders alongside this one. Cookie-Script injects asynchronously via
  // GTM and may not be in the DOM yet on mount, so we both (a) flag the root for
  // the scoped CSS rule that hides the wrapper the instant it appears, and (b)
  // run a MutationObserver as the JS fallback that hides it and cleans up.
  useEffect(() => {
    if (!isManagedHost) {
      return;
    }

    const root = document.documentElement;

    root.classList.add(COOKIESCRIPT_SUPPRESSED_CLASS);

    const stopObserving = suppressCookieScriptBanner();

    return () => {
      stopObserving();
      root.classList.remove(COOKIESCRIPT_SUPPRESSED_CLASS);
    };
  }, [isManagedHost]);

  if (!isManagedHost || isConsentGiven) {
    return <></>;
  }

  const t = spawn('consent-banner');

  const onChooseConsent = (
    value: (typeof CONSENT_VALUE)[keyof typeof CONSENT_VALUE],
  ) => {
    storeConsent(value);
    setIsConsentGiven(true);
  };

  // Portal to <body> so the fixed banner floats above app content regardless
  // of the router subtree's stacking contexts (matches how the third-party
  // banner and DS dialogs escape the layout).
  return createPortal(
    <div
      role="dialog"
      aria-label={t('aria-label')}
      className={cn(
        'fixed bottom-4 right-4 z-banner w-[min(92vw,26rem)]',
        'flex flex-col gap-3 rounded-lg border border-border-panel-neutral',
        'bg-fill-bg-neutral p-4 shadow-lg',
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <span className="text-sm font-medium text-text-bg-neutral-strongest">
          {t('title')}
        </span>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('dismiss')}
          onClick={() => onChooseConsent(CONSENT_VALUE.DECLINED)}
        >
          <X className="size-4" />
        </Button>
      </div>
      <p className="text-xs text-text-general-neutral-mid">
        {t('description')}{' '}
        <Button
          variant="link"
          size="xs"
          className="inline px-0"
          onClick={() => openUrl(PRIVACY_POLICY_URL, true)}
        >
          {t('read-more')}
        </Button>
      </p>
      <div className="flex justify-start gap-2">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onChooseConsent(CONSENT_VALUE.DECLINED)}
        >
          {t('decline-all')}
        </Button>
        <Button
          variant="primary"
          size="sm"
          onClick={() => onChooseConsent(CONSENT_VALUE.ACCEPTED)}
        >
          {t('accept-all')}
        </Button>
      </div>
    </div>,
    document.body,
  );
};
